import { DAY_NAMES, MONTH_NAMES } from '../config/constants';
import { GAME_SPEEDS, type GameSpeed } from '../config/gameConfig';
import { GameClock } from '../simulation/GameClock';
import type { GameStore } from '../simulation/GameStore';
import type { GameState } from '../models/GameState';
import { formatCurrency, formatCurrencyCompact } from '../utils/formatCurrency';
import { Dashboard } from './Dashboard';
import { t } from './i18n';

/**
 * HUD: the top status bar + speed controls, rendered as an HTML/CSS overlay
 * on top of the Phaser canvas (spec: "HTML / CSS Overlay + Phaser", section 2;
 * main HUD, section 24).
 *
 * It is a pure view: it subscribes to the GameStore and reflects state, and it
 * calls store actions on user input. It holds no game logic.
 */
export class HUD {
  private readonly root: HTMLElement;
  private readonly store: GameStore;
  private readonly unsubscribe: () => void;

  // Cached element references, updated on each state change.
  private cashEl!: HTMLElement;
  private valueEl!: HTMLElement;
  private revenueEl!: HTMLElement;
  private pnlEl!: HTMLElement;
  private dateEl!: HTMLElement;
  private timeEl!: HTMLElement;
  private repEl!: HTMLElement;
  private speedButtons = new Map<GameSpeed, HTMLButtonElement>();

  private onMenu?: () => void;
  private dashboard?: Dashboard;
  /** Id of the last notification surfaced, so each is toasted once. */
  private lastNotificationId?: string;

  constructor(root: HTMLElement, store: GameStore, onMenu?: () => void) {
    this.root = root;
    this.store = store;
    this.onMenu = onMenu;

    this.buildDom();
    this.injectStyles();
    this.unsubscribe = this.store.subscribe((state) => this.render(state));
  }

  destroy(): void {
    this.dashboard?.destroy();
    this.unsubscribe();
    if (this.container && this.container.parentElement) {
      this.container.parentElement.removeChild(this.container);
    }
  }

  private container!: HTMLElement;

  private buildDom(): void {
    const container = document.createElement('div');
    // NOTE: the container spans the full screen but must NOT capture pointer
    // events (that would block clicks to the Phaser canvas underneath). Only
    // the top bar strip is interactive — see .pt-topbar { pointer-events }.
    container.className = 'pt-hud';

    // --- top bar: cash / date / time / reputation ---
    const topBar = document.createElement('div');
    topBar.className = 'pt-topbar';

    this.cashEl = this.makeStat(topBar, '💰', 'cash');
    this.valueEl = this.makeStat(topBar, '🏢', 'value');
    this.revenueEl = this.makeStat(topBar, '📈', 'revenue');
    this.pnlEl = this.makeStat(topBar, '📊', 'pnl');
    this.dateEl = this.makeStat(topBar, '📅', 'date');
    this.timeEl = this.makeStat(topBar, '🕗', 'time');
    this.repEl = this.makeStat(topBar, '⭐', 'reputation');

    // --- speed controls ---
    const speedBar = document.createElement('div');
    speedBar.className = 'pt-speedbar';

    const speedLabels: Record<GameSpeed, string> = {
      0: '⏸',
      1: '1x',
      2: '2x',
      4: '4x',
      8: '8x',
    };

    for (const speed of GAME_SPEEDS) {
      const btn = document.createElement('button');
      btn.className = 'pt-speed-btn';
      btn.textContent = speedLabels[speed];
      btn.title = speed === 0 ? 'Pause' : `Speed ${speed}x`;
      btn.addEventListener('click', () => this.store.setSpeed(speed));
      this.speedButtons.set(speed, btn);
      speedBar.appendChild(btn);
    }

    topBar.appendChild(speedBar);

    // --- dashboard button ---
    const dashBtn = document.createElement('button');
    dashBtn.className = 'pt-menu-btn';
    dashBtn.textContent = '📊';
    dashBtn.title = 'Dashboard';
    dashBtn.addEventListener('click', () => {
      if (!this.dashboard) this.dashboard = new Dashboard(this.root, this.store);
      this.dashboard.toggle();
    });
    topBar.appendChild(dashBtn);

    // --- save button ---
    const saveBtn = document.createElement('button');
    saveBtn.className = 'pt-menu-btn';
    saveBtn.textContent = t('hud.save');
    saveBtn.addEventListener('click', () => {
      const ok = this.store.saveGame();
      this.showToast(ok ? t('hud.saved') : t('hud.saveFail'), ok);
    });
    topBar.appendChild(saveBtn);

    // --- menu button ---
    const menuBtn = document.createElement('button');
    menuBtn.className = 'pt-menu-btn';
    menuBtn.textContent = t('hud.menu');
    menuBtn.addEventListener('click', () => this.onMenu?.());
    topBar.appendChild(menuBtn);

    container.appendChild(topBar);
    this.container = container;
    this.root.appendChild(container);
  }

  /** Transient message under the top bar (e.g. save confirmation). */
  private showToast(message: string, ok = true): void {
    const toast = document.createElement('div');
    toast.className = 'pt-toast';
    toast.textContent = message;
    toast.style.background = ok ? 'rgba(30,80,40,0.95)' : 'rgba(90,30,30,0.95)';
    this.container.appendChild(toast);
    setTimeout(() => toast.remove(), 1800);
  }

  private makeStat(
    parent: HTMLElement,
    icon: string,
    labelKey: string
  ): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'pt-stat';
    wrap.dataset.key = labelKey;

    const iconEl = document.createElement('span');
    iconEl.className = 'pt-stat-icon';
    iconEl.textContent = icon;

    const valueEl = document.createElement('span');
    valueEl.className = 'pt-stat-value';
    valueEl.textContent = '—';

    wrap.appendChild(iconEl);
    wrap.appendChild(valueEl);
    parent.appendChild(wrap);
    return valueEl;
  }

  private render(state: GameState): void {
    const { player, time, finance } = state;

    this.cashEl.textContent = formatCurrency(player.cash);
    this.valueEl.textContent = formatCurrencyCompact(player.companyValue);

    // Today's accrued parking revenue and the last settled day's profit/loss.
    this.revenueEl.textContent = formatCurrency(finance.todayRevenue);
    const last = finance.history[finance.history.length - 1];
    if (last) {
      const sign = last.profit >= 0 ? '+' : '';
      this.pnlEl.textContent = `${sign}${formatCurrency(last.profit)}`;
      this.pnlEl.style.color = last.profit >= 0 ? '#a5d6a7' : '#ef9a9a';
    } else {
      this.pnlEl.textContent = '—';
      this.pnlEl.style.color = '#b0bec5';
    }

    const dow = DAY_NAMES[time.dayOfWeek] ?? '';
    const mon = MONTH_NAMES[time.month - 1] ?? '';
    this.dateEl.textContent = `Y${time.year} ${mon} ${time.day} (${dow})`;

    this.timeEl.textContent = GameClock.formatTime(time);
    this.repEl.textContent = `${Math.round(player.reputation)}/100`;

    // Surface the newest notification (event alerts) once each.
    const notes = state.notifications;
    if (notes.length > 0) {
      const latest = notes[notes.length - 1];
      if (latest.id !== this.lastNotificationId) {
        this.lastNotificationId = latest.id;
        this.showToast(latest.message, latest.kind !== 'negative');
      }
    }

    // Highlight the active speed; when paused, highlight the pause button.
    const activeSpeed: GameSpeed = state.paused ? 0 : state.speed;
    for (const [speed, btn] of this.speedButtons) {
      btn.classList.toggle('active', speed === activeSpeed);
    }
  }

  private injectStyles(): void {
    const STYLE_ID = 'pt-hud-styles';
    if (document.getElementById(STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .pt-hud { position: absolute; inset: 0; pointer-events: none; font-family: 'Segoe UI', Tahoma, sans-serif; }
      /* Only the top strip is interactive so the rest passes clicks to the canvas. */
      .pt-topbar {
        pointer-events: auto;
        position: absolute; top: 0; left: 0; right: 0;
        display: flex; align-items: center; gap: 18px;
        padding: 10px 16px;
        background: linear-gradient(180deg, rgba(11,18,32,0.96), rgba(11,18,32,0.80));
        border-bottom: 1px solid rgba(255,255,255,0.08);
        color: #eceff1;
      }
      .pt-stat { display: flex; align-items: center; gap: 6px; font-size: 15px; }
      .pt-stat-icon { font-size: 16px; }
      .pt-stat-value { font-weight: 600; letter-spacing: 0.3px; }
      .pt-stat[data-key="cash"] .pt-stat-value { color: #ffd54f; }
      .pt-speedbar { display: flex; gap: 4px; margin-left: auto; }
      .pt-speed-btn {
        min-width: 40px; padding: 6px 10px;
        background: #1a2333; color: #cfd8dc;
        border: 1px solid rgba(255,255,255,0.12); border-radius: 6px;
        cursor: pointer; font-size: 14px; font-weight: 600;
        transition: background 0.12s, color 0.12s;
      }
      .pt-speed-btn:hover { background: #26334a; }
      .pt-speed-btn.active { background: #1e88e5; color: #fff; border-color: #64b5f6; }
      .pt-menu-btn {
        padding: 6px 12px; background: #263238; color: #eceff1;
        border: 1px solid rgba(255,255,255,0.12); border-radius: 6px;
        cursor: pointer; font-size: 14px; font-weight: 600;
      }
      .pt-menu-btn:hover { background: #37474f; }
      .pt-toast {
        position: absolute; top: 60px; left: 50%; transform: translateX(-50%);
        padding: 10px 18px; border-radius: 8px; color: #fff; font-size: 15px;
        font-weight: 600; pointer-events: none; z-index: 20;
        box-shadow: 0 4px 16px rgba(0,0,0,0.4);
      }
    `;
    document.head.appendChild(style);
  }
}
